import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { AuthUser } from '@pointage/shared';
import { useLogout, useMe } from '../api/hooks.js';
import { ImpersonateModal } from './ImpersonateModal.js';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]!.toUpperCase()).join('') || '?';
}

interface Props {
  user: AuthUser;
}

export function UserMenu({ user }: Props) {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const logout = useLogout();
  const me = useMe();
  const navigate = useNavigate();
  // « Voir comme » : réservé à qui gère les comptes, et pas de
  // deuxième saut d'identité depuis une impersonation en cours.
  const canImpersonate =
    (me.data?.permissions ?? []).includes('users.manage') && !me.data?.impersonating;

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onSignOut = async () => {
    setOpen(false);
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  };

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu-trigger"
      >
        <span className="user-avatar" aria-hidden="true">{initialsOf(user.name)}</span>
        <span className="user-menu-name">{user.name}</span>
      </button>
      {open && (
        <div className="user-menu-panel" role="menu" data-testid="user-menu-panel">
          <div className="user-menu-role">
            Acting as <strong>{user.name}</strong> · {user.role}
          </div>
          {canImpersonate && (
            <button
              type="button"
              className="user-menu-item"
              onClick={() => {
                setOpen(false);
                setPickerOpen(true);
              }}
              role="menuitem"
              data-testid="user-menu-impersonate"
            >
              Voir comme…
            </button>
          )}
          <button
            type="button"
            className="user-menu-item"
            onClick={onSignOut}
            role="menuitem"
            data-testid="user-menu-signout"
          >
            Sign out
          </button>
        </div>
      )}
      <ImpersonateModal
        open={pickerOpen}
        currentUserId={user.id}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
